import React, { useEffect, useRef, useContext, useMemo } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import moment from 'moment';

import LabelName from 'components/UI/LabelName/LabelName';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import SelectedAttendees from './SelectedAttendees';
import SelectedGroups from './SelectedGroups';
import SelectedFirmUsers from './SelectedFirmUsers';
import SuggestedTime from 'modules/calendar/Components/SuggestedTime/SuggestedTime';
import useEventFreeTime from 'hooks/useEventFreeTime';
import { generateFullName } from 'utils/generateFullNameUtils';
import { UserDetailsContext } from 'context/userDetailsContext';
import { currentBrowserTimeZone } from 'utils/utility_functions/timezone';
import { assignToItemTemplate, checkOptionDisabled } from './attendeeUtils';
import { useCalendarUI } from 'modules/calendarv2/context/CalendarContext';

function EventAttendees({ control, getValues, setValue, trigger, watch, attendeesList = [], isLoadingAttendees = false, errors = {} }) {
  const op = useRef(null);
  const userDetails = useContext(UserDetailsContext);
  const { eventFreeTime, loadFreeTime, loading } = useEventFreeTime();
  const { eventFormMode } = useCalendarUI();
  const notLinkedToCase = watch('not_linked_to_case');

  const { fields, replace: contactsReplaceAll } = useFieldArray({
    control,
    name: 'contacts',
  });

  const { fields: groupsFields, replace: groupsReplaceAll } = useFieldArray({
    control,
    name: 'groups',
  });

  const { fields: firm_UsersFields, replace: firm_UsersReplaceAll } = useFieldArray({
    control,
    name: 'firm_users',
  });

  const selectedAttendeeList = useMemo(() => {
    return [...fields, ...groupsFields, ...firm_UsersFields];
  }, [fields, groupsFields, firm_UsersFields]);

  const eventColorInfo = useMemo(() => {
    const firmUsersArray = attendeesList?.find((item) => item.label === 'Firm Users')?.items || [];

    if (firm_UsersFields.length > 0) {
      const firstFirmUser = firm_UsersFields[0];
      const fullUserData = firmUsersArray.find((u) => u.firm_user_id === firstFirmUser.firm_user_id);
      return {
        color: fullUserData?.calendar_color || '#3b82f6',
        userName: firstFirmUser.name || 'First attendee',
      };
    }

    return {
      color: '#3b82f6',
      userName: null,
    };
  }, [firm_UsersFields, attendeesList, userDetails?.firmDetails?.client_cognito_username]);

  useEffect(() => {
    // to add logged user in the attendees array
    if (attendeesList && eventFormMode === 'create') {
      const currentUser = userDetails?.firmDetails?.client_cognito_username;
      if (currentUser) {
        setValue('selected_attendees_list', [currentUser]);
        handleAttendeesChange([currentUser]);
      }
    }
  }, [attendeesList, eventFormMode]);

  function handleAttendeesChange(value) {
    const usersArray = attendeesList?.find((item) => item.label === 'Contacts')?.items;
    const groupsArray = attendeesList?.find((item) => item.label === 'Groups')?.items;
    const firm_usersArray = attendeesList?.find((item) => item.label === 'Firm Users')?.items;

    const selectedFirmUsersArray = firm_usersArray?.filter((item) => value.includes(item.client_cognito_username));
    const selectedUsersArray = usersArray?.filter(
      (item) => value.includes(item.contact_id) || value.includes(item.client_cognito_username)
    );
    const selectedGroupsArray = groupsArray?.filter((item) => value.includes(item.user_group_id));

    // Handle Contacts - merge existing with new
    if (selectedUsersArray?.length >= 0) {
      // Start with existing contacts
      const existingContacts = [...fields];
      const newContactsToAdd = [];

      selectedUsersArray?.forEach((currentSelection) => {
        const userExists = existingContacts.some(
          (field) => field.contact_id === currentSelection.contact_id || field.cognito_username === currentSelection.client_cognito_username
        );

        // Only add if doesn't exist
        if (!userExists) {
          newContactsToAdd.push({
            name: generateFullName(currentSelection),
            email: currentSelection.email,
            is_attendance_required: 'optional',
            send_reminder: false,
            cognito_username: currentSelection.client_cognito_username
              ? currentSelection.client_cognito_username
              : currentSelection.cognito_username,
            contact_id: currentSelection.contact_id || '',
            access_level: currentSelection?.access_level,
            profile_image: currentSelection?.profile_image,
          });
        }
      });

      // Merge existing + new contacts
      const mergedContacts = [...existingContacts, ...newContactsToAdd];

      // Filter to keep only selected contacts (remove deselected ones)
      const selectedContactIds = selectedUsersArray?.map((u) => u.contact_id) || [];
      const filteredContacts = mergedContacts.filter((contact) => selectedContactIds.includes(contact.contact_id));

      contactsReplaceAll(filteredContacts);
    }

    // Handle Firm Users - merge existing with new
    if (selectedFirmUsersArray?.length >= 0) {
      // Start with existing firm users
      const existingFirmUsers = [...firm_UsersFields];
      const newFirmUsersToAdd = [];

      selectedFirmUsersArray?.forEach((currentSelection) => {
        const firm_userExists = existingFirmUsers.some((field) => field.firm_user_id === currentSelection.firm_user_id);

        // Only add if doesn't exist
        if (!firm_userExists) {
          newFirmUsersToAdd.push({
            name: generateFullName(currentSelection),
            email: currentSelection.email,
            is_attendance_required: 'optional',
            send_reminder: false,
            cognito_username: currentSelection.client_cognito_username
              ? currentSelection.client_cognito_username
              : currentSelection.cognito_username,
            contact_id: currentSelection.contact_id || '',
            access_level: currentSelection?.access_level,
            profile_image: currentSelection?.profile_image,
            firm_user_id: currentSelection.firm_user_id || '',
          });
        }
      });

      // Merge existing + new firm users
      const mergedFirmUsers = [...existingFirmUsers, ...newFirmUsersToAdd];

      // Filter to keep only selected firm users (remove deselected ones)
      const selectedFirmUserIds = selectedFirmUsersArray?.map((u) => u.firm_user_id) || [];
      const filteredFirmUsers = mergedFirmUsers.filter((user) => selectedFirmUserIds.includes(user.firm_user_id));

      firm_UsersReplaceAll(filteredFirmUsers);
    }

    // Handle Groups - merge existing with new
    if (selectedGroupsArray?.length >= 0) {
      // Start with existing groups
      const existingGroups = [...groupsFields];
      const newGroupsToAdd = [];

      selectedGroupsArray?.forEach((currentSelection) => {
        const groupExists = existingGroups.some((field) => field.group_id === currentSelection.user_group_id);

        // Only add if doesn't exist
        if (!groupExists) {
          newGroupsToAdd.push({
            group_id: currentSelection?.user_group_id,
            group_name: currentSelection?.group_name,
            sk: currentSelection?.sk,
            is_attendance_required: 'optional',
            send_reminder: false,
            user_accessible_list: currentSelection?.user_accessible_list,
            res_type: 'user-group',
          });
        }
      });

      // Merge existing + new groups
      const mergedGroups = [...existingGroups, ...newGroupsToAdd];

      // Filter to keep only selected groups (remove deselected ones)
      const selectedGroupIds = selectedGroupsArray?.map((g) => g.user_group_id) || [];
      const filteredGroups = mergedGroups.filter((group) => selectedGroupIds.includes(group.group_id));

      groupsReplaceAll(filteredGroups);
    }
  }

  const getSuggestedTime = async (e) => {
    const selectedDate = moment.tz(getValues('when'), userDetails?.userDetails?.timezone);

    // Get the time from meeting_start_time and meeting_end_time
    const startTime = moment.tz(getValues('meeting_start_time'), userDetails?.userDetails?.timezone);
    const endTime = moment.tz(getValues('meeting_end_time'), userDetails?.userDetails?.timezone);

    // Combine date from 'when' with time from meeting_start_time
    const combinedStartTime = moment.tz(
      {
        year: selectedDate.year(),
        month: selectedDate.month(),
        date: selectedDate.date(),
        hour: startTime.hour(),
        minute: startTime.minute(),
        second: startTime.second(),
        millisecond: 0,
      },
      userDetails?.userDetails?.timezone
    );

    // Combine date from 'when' with time from meeting_end_time
    let combinedEndTime = moment.tz(
      {
        year: selectedDate.year(),
        month: selectedDate.month(),
        date: selectedDate.date(),
        hour: endTime.hour(),
        minute: endTime.minute(),
        second: endTime.second(),
        millisecond: 0,
      },
      userDetails?.userDetails?.timezone
    );

    // Check if end time is before or equal to start time (indicates next day)
    if (combinedEndTime.isSameOrBefore(combinedStartTime)) {
      combinedEndTime.add(1, 'day');
    }

    await loadFreeTime({
      user_list: selectedAttendeeList.flatMap((attendee) =>
        attendee?.res_type === 'user-group' ? attendee?.user_accessible_list : [attendee?.cognito_username]
      ),
      start_date: selectedDate,
      end_date: moment.tz(selectedDate, userDetails?.userDetails?.timezone).add(2, 'days').endOf('day'),
      meeting_start_time: combinedStartTime,
      meeting_end_time: combinedEndTime,
      response_limit: 5,
    });
    if (op.current) op.current.toggle(e);
  };

  const handleSelectTime = (value) => {
    const offset1 = moment.tz(value, currentBrowserTimeZone).utcOffset();
    const offset2 = moment.tz(value, userDetails?.userDetails?.timezone).utcOffset();
    const differenceInMinutes = offset1 - offset2;
    const date = moment(value).subtract(differenceInMinutes, 'minutes').toDate();

    setValue('when', date);
    setValue('meeting_start_time', date);
    setValue('meeting_end_time', moment(date).tz(userDetails?.userDetails?.timezone).add(1, 'hour').toDate());
    trigger('when');
    trigger('meeting_start_time');
    trigger('meeting_end_time');
    op.current?.hide();
  };

  return (
    <>
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-md-4 col-12">
          <LabelName required={true} htmlFor="selected_attendees_list">
            Add Attendees
          </LabelName>
        </div>

        <div className="col-md-8 col-12 add-attendees">
          <Controller
            name="selected_attendees_list"
            control={control}
            defaultValue={[]}
            rules={{ required: 'Please select at least one attendee' }}
            render={({ field }) => {
              const filteredOptions = (attendeesList ?? []).filter((group) => {
                if (notLinkedToCase && group.label === 'Contacts') {
                  return false;
                }
                return true;
              });

              return (
                <MultiSelect
                  options={filteredOptions.sort((a, b) => {
                    const groupOrder = ['Firm Users', 'Contacts', 'Groups'];
                    const aIndex = groupOrder.indexOf(a.label);
                    const bIndex = groupOrder.indexOf(b.label);
                    return aIndex - bIndex;
                  })}
                  optionLabel="label"
                  optionGroupLabel="label"
                  optionGroupChildren="items"
                  optionValue={(item) => item?.client_cognito_username || item?.contact_id || item?.user_group_id}
                  display="chip"
                  className="input-shadow w-100"
                  filter
                  id={field?.name}
                  value={attendeesList?.length > 0 && field?.value}
                  placeholder={isLoadingAttendees ? 'Loading attendees...' : 'Select Attendees'}
                  disabled={isLoadingAttendees}
                  onChange={(e) => {
                    field.onChange(e.value);
                    handleAttendeesChange(e.value);
                  }}
                  emptyFilterMessage="No records found"
                  optionDisabled={checkOptionDisabled}
                  itemTemplate={assignToItemTemplate}
                  showSelectAll={false}
                />
              );
            }}
          />
        </div>
        <div className="offset-md-4 col-8">
          <RequiredValidation field="selected_attendees_list" errors={errors} />
        </div>
      </div>

      {/* Event Color Indicator - Shows when firm users are selected */}
      {firm_UsersFields.length > 0 && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName htmlFor="event_color">Event Color</LabelName>
          </div>
          <div className="col-md-8 col-12">
            <div className="event-color-indicator d-flex align-items-center gap-2">
              <div
                className="event-color-swatch"
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '6px',
                  backgroundColor: eventColorInfo.color,
                  border: '2px solid rgba(0,0,0,0.1)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                }}
              />
              <span className="text-muted F-size13">
                <>{eventColorInfo.userName}'s calendar color</>
              </span>
              <i className="pi pi-info-circle text-muted event-color-info-icon" style={{ fontSize: '14px', cursor: 'help' }} />
              <Tooltip target=".event-color-info-icon" position="top" className="event-color-tooltip">
                <div style={{ maxWidth: '280px', padding: '4px' }}>
                  <strong style={{ display: 'block', marginBottom: '4px' }}>
                    The calendar event color is determined by the selection of the first firm attendee.
                  </strong>
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
      )}

      <div className="d-flex justify-content-end">
        <Button
          type="button"
          label="Suggest Time"
          className="btn-outline p-button-secondary outline"
          onClick={getSuggestedTime}
          aria-controls="popup_menu"
          aria-haspopup
          loading={loading}
          disabled={selectedAttendeeList?.length < 2}
          tooltip="Suggested time will be within business hours, in 1-hour intervals."
          tooltipOptions={{ position: 'top', showDelay: 500 }}
        />
        <SuggestedTime {...{ handleSelectTime, eventFreeTime, op, timezone: userDetails?.userDetails?.timezone }} />
      </div>

      {groupsFields?.length > 0 && <SelectedGroups selectedGroups={groupsFields} control={control} watch={watch} setValue={setValue} />}
      {firm_UsersFields?.length > 0 && (
        <SelectedFirmUsers selectedAttendees={firm_UsersFields} control={control} watch={watch} setValue={setValue} />
      )}
      {fields?.length > 0 && <SelectedAttendees selectedAttendees={fields} control={control} watch={watch} setValue={setValue} />}
    </>
  );
}

export default EventAttendees;
